import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { WarningCircle } from "@phosphor-icons/react";

const fmtMeta = (detail) => {
  const dup = detail?.duplicate_of || {};
  const parts = [];
  // Field names vary by producer: ingest sends doc_type/filed_at,
  // bills send statement_date, minerals send check_date.
  const when = dup.filed_at || dup.statement_date || dup.check_date || dup.created_at;
  if (when) parts.push(String(when).slice(0, 10));
  if (dup.doc_type) parts.push(dup.doc_type.replaceAll("_", " "));
  return parts.join(" · ");
};

export default function ConfirmDuplicateDialog({
  open,
  onOpenChange,
  detail,
  busy = false,
  onConfirm,
  onView,
}) {
  if (!detail?.duplicate_of) return null;

  const duplicateOf = detail.duplicate_of;
  const meta = fmtMeta(detail);
  const name = duplicateOf.filename || duplicateOf.label || "this document";
  const canView = duplicateOf.archive_source && duplicateOf.archive_item_id;
  const title = `Already filed as ${name}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="confirm-duplicate-dialog"
        className="rounded-sm max-w-lg border-2 border-[hsl(var(--ploy-rust))]"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black flex items-center gap-2 text-[hsl(var(--ploy-ink))]">
            <WarningCircle size={24} weight="fill" className="text-[hsl(var(--ploy-rust))]" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-[hsl(var(--ploy-ink-soft))]">
            {detail.message || "This document looks like a duplicate of one that was already filed."}
          </p>
          {duplicateOf.filename && (
            <p className="text-[13px] font-semibold text-[hsl(var(--ploy-ink))] break-words">
              Matching document: {duplicateOf.filename}
            </p>
          )}
          {meta && (
            <div className="rounded-sm border border-[hsl(var(--ploy-border))] bg-[hsl(var(--ploy-panel))] px-3 py-2 text-[12px] font-medium text-[hsl(var(--ploy-ink))]">
              {meta}
            </div>
          )}
          {canView && (
            <button
              type="button"
              data-testid="confirm-duplicate-view"
              onClick={() => onView?.(duplicateOf)}
              className="text-[12px] font-semibold text-[hsl(var(--ploy-rust))] hover:underline"
            >
              View matching document
            </button>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button
            data-testid="confirm-duplicate-cancel"
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
          >
            Cancel
          </Button>
          {detail.overridable && (
            <Button
              data-testid="confirm-duplicate-anyway"
              type="button"
              disabled={busy}
              onClick={onConfirm}
              className="rounded-sm bg-[hsl(var(--ploy-ink))] hover:bg-[hsl(var(--ploy-ink))]/90 text-[hsl(var(--ploy-ink-inv))]"
            >
              {busy ? "Confirming…" : "Confirm anyway"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
